/**
 * ChatDashboard - Dashboard principal do chat com widgets e tabela
 */

import React, { useState } from 'react';
import { ChatDashboardWidget } from './ChatDashboardWidget';
import { ChatDashboardTable } from './ChatDashboardTable';
import { useChatDashboard } from '../hooks';
import { Icon } from '@/shared/icons'
interface ChatDashboardProps {
  onFilterChange?: (filter: FilterType | null) => void;
}

export type FilterType = 'new' | 'inProgress' | 'waiting' | 'returned';

export const ChatDashboard: React.FC<ChatDashboardProps> = ({ onFilterChange }) => {
  const [activeFilter, setActiveFilter] = useState<FilterType | null>(null);

  // Buscar dados reais da API
  const { data, isLoading, error } = useChatDashboard();

  const handleWidgetClick = (filter: FilterType) => {
    const newFilter = activeFilter === filter ? null : filter;
    setActiveFilter(newFilter);
    onFilterChange?.(newFilter);
  };

  // Loading state
  if (isLoading) {
    return (
      <div
        className="flex-1 flex flex-col items-center justify-center"
        style={{ backgroundColor: 'var(--theme-page-background)' }}
      >
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
        <p className="mt-4 text-sm" style={{ color: 'var(--theme-muted-text)' }}>
          Carregando estatísticas...
        </p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div
        className="flex-1 flex flex-col items-center justify-center"
        style={{ backgroundColor: 'var(--theme-page-background)' }}
      >
        <Icon name="alert-circle" className="w-12 h-12 mb-4" style={{ color: 'var(--theme-error-color)' }} />
        <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
          Erro ao carregar estatísticas
        </p>
      </div>
    );
  }

  // Empty state
  if (!data) {
    return (
      <div
        className="flex-1 flex flex-col items-center justify-center"
        style={{ backgroundColor: 'var(--theme-page-background)' }}
      >
        <p className="text-sm" style={{ color: 'var(--theme-muted-text)' }}>
          Nenhuma estatística disponível
        </p>
      </div>
    );
  }

  return (
    <div
      className="flex-1 flex flex-col p-6 overflow-y-auto"
      style={{ backgroundColor: 'var(--theme-page-background)' }}
    >
      {/* Widgets */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <ChatDashboardWidget
          title="Novas Mensagens"
          value={data.totals.newMessages}
          icon={'message-circle'}
          color="primary"
          onClick={() => handleWidgetClick('new')}
          isActive={activeFilter === 'new'}
        />
        <ChatDashboardWidget
          title="Em Atendimento"
          value={data.totals.inProgress}
          icon={'user-check'}
          color="warning"
          onClick={() => handleWidgetClick('inProgress')}
          isActive={activeFilter === 'inProgress'}
        />
        <ChatDashboardWidget
          title="Em Espera"
          value={data.totals.waiting}
          icon={'clock'}
          color="success"
          onClick={() => handleWidgetClick('waiting')}
          isActive={activeFilter === 'waiting'}
        />
        <ChatDashboardWidget
          title="Retorno"
          value={data.totals.returned}
          icon={'alert-circle'}
          color="error"
          onClick={() => handleWidgetClick('returned')}
          isActive={activeFilter === 'returned'}
        />
      </div>

      {/* Tabela */}
      <ChatDashboardTable departments={data.teams} individualUsers={data.individualUsers} />
    </div>
  );
};
